import React, { useLayoutEffect, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import Footer from './components/layout/Footer';
import DashboardPage from './pages/DashboardPage';
import ZonesPage from './pages/ZonesPage';
import BaseUnitsPage from './pages/BaseUnitsPage';
import SensorsPage from './pages/SensorsPage';
import AlarmsPage from './pages/AlarmsPage';
import SensorDetailsPage from './pages/SensorDetailsPage';
import LoginPage from './pages/LoginPage';
import Login2Page from './pages/Login2Page';
import UsersPage from './pages/UsersPage';
import type { RootState } from './store/store';
import { restoreAuth } from './store/slices/authSlice';
import { setBranding, BrandingState } from './store/slices/brandingSlice';
import { API_CONFIG } from './config/api.config';
import { prepareCommonHeaders } from './services/baseApi';
import { useAuth } from './hooks/useAuth';
import { useDashboardPolling } from './hooks/useDashboardPolling';
import LoadingOverlay from './components/common/LoadingOverlay';
import PlanViewPage from './pages/PlanViewPage';
import SitesPage from './pages/SitesPage';
import AlarmLogPage from './pages/AlarmLogPage';
import HelpPage from './pages/HelpPage';
import SupportPage from './pages/SupportPage';
import RemoteAlertsPage from './pages/RemoteAlertsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import DataProcessingAgreementPage from './pages/DataProcessingAgreementPage';
import CompliancePage from './pages/CompliancePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccessControlPage from './pages/AccessControlPage';
// import BillingPage from './pages/BillingPage';
import SuperAdminPage from './pages/SuperAdminPage';
import ValidationDocsPage from './pages/ValidationDocsPage';

function AppContent() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isDark = useSelector((state: RootState) => state.theme.isDark);
  const brandColor = useSelector((state: RootState) => state.branding.brandColor);
  
  // Add error state
  const [error, setError] = useState<Error | null>(null);

  useLayoutEffect(() => {
    try {
      dispatch(restoreAuth());
    } catch (err) {
      console.error('Failed to restore auth:', err);
      setError(err as Error);
    }
  }, [dispatch]);

  // Handle theme changes
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Apply brand colour as a CSS variable so dynamic theming works app-wide
  useEffect(() => {
    document.documentElement.style.setProperty('--brand-color', brandColor || '#4f46e5');
  }, [brandColor]);

  useAuth(); // Hook for token verification
  useDashboardPolling(); // Route-aware polling for getLiveData and backfill-status

  useEffect(() => {
    document.body.classList.add('reading-boost-light');
    return () => document.body.classList.remove('reading-boost-light');
  }, []);

  // Load branding when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const loadBranding = async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/company-settings`, {
          method: 'GET',
          headers: prepareCommonHeaders(),
          credentials: 'include',
        });
        if (res.ok) {
          const d = await res.json();
          dispatch(setBranding({
            companyName: d.company_name || '',
            logoUrl: d.logo_url || '',
            brandColor: d.brand_color || '#4f46e5',
            emailAddress: d.email_address || '',
            phone: d.phone || '',
            website: d.website || '',
            reportHeader: d.report_header || '',
            emailFooter: d.email_footer || '',
          }));
        }
      } catch {
        // Non-fatal — branding just falls back to defaults
      }
    };
    loadBranding();
  }, [isAuthenticated, dispatch]);

  if (error) {
    return <div>Error initializing app: {error.message}</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login2" element={<Login2Page />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/dpa" element={<DataProcessingAgreementPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Add mobile menu state */}
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <div className="min-h-screen flex flex-col bg-transparent dark:bg-transparent">
              <Header />
              <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 overflow-y-auto w-full">
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/dashboard/plan" element={<PlanViewPage />} />
                    <Route path="/sensors" element={<SensorsPage />} />
                    <Route path="/sensors/:sensorId" element={<SensorDetailsPage />} />
                    <Route path="/alarms" element={<AlarmsPage />} />
                    <Route path="/remote-alerts" element={<RemoteAlertsPage />} />
                    <Route path="/base-units" element={<BaseUnitsPage />} />
                    <Route path="/zones" element={<ZonesPage />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/audit-logs" element={<AuditLogsPage />} />
                    <Route path="/alarm-log" element={<AlarmLogPage />} />
                    <Route path="/help" element={<HelpPage />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/access-control" element={<AccessControlPage />} />
                    {/* <Route path="/billing" element={<BillingPage />} /> */}
                    <Route path="/super-admin" element={<SuperAdminPage />} />
                    <Route path="/validation-docs" element={<ValidationDocsPage />} />
                  </Routes>
                </main>
              </div>
              <Footer />
            </div>
          ) : (
            <LoginPage />
          )
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <>
      <LoadingOverlay />
      <Router>
        <AppContent />
      </Router>
    </>
  );
}

export default App;